/**
 * BlueBean「対応履歴」(report_detail_10_YYYY-M-D.csv)の列検証・正規化・集約。
 * 定義は現行日報シート(2026-09-09)と一致することを確認したもの(docs/specs/bluebean-import.md §0-2・§0-3):
 *  - 取得 = リアクション情報「アポ〇」の行を、電話番号(最新)(無ければ顧客ID)で **その日1件** に(オペレーター横断、最後に更新した行を採用)
 *  - 時間帯 = 採用した行の「更新日」の時台。9〜19時台の枠外(8時台など)は合計にだけ入る
 *  - BB ID = 登録者ID、会社 = 業務コード(最新) → 会社マスタの BB業務コード(無ければ社名で照合)
 *  - 対応者情報=キャンセル も取得に含める(現行と同じ。CXL は別運用)
 *  - 通話回数・通話時間・稼働は OPレポートを正とし、ここからは取得件数・時間帯・会社だけを取る
 * 顧客の個人情報(名前・電話番号・住所)は重複判定にメモリ上で使うだけで、集約結果には残さない。
 */
import { HOUR_BANDS, type Company, type DateStr } from '@core/lib/types';
import { normalizeBbId } from './opReport';
import type { ActivityStat, ColumnMapping, CsvImporter, ImportFileInfo, ImportIssue, ImportResult, ParsedActivityRow, RawRow } from './types';

/** 内部キー → 実CSVの列名(43列のうち使う列) */
export const ACTIVITY_COLUMNS = {
  at: '発着信時間',
  updated_at: '更新日',
  reaction: 'リアクション情報',
  contact: '対応者情報',
  bb_id: '登録者ID',
  operator_name: '登録者名',
  business_code: '業務コード(最新)',
  phone: '電話番号(最新)',
  customer_id: '顧客ID(最新)',
} as const;

/**
 * 顧客情報レポートのもう一つの出力形(2026-09-10 の 9/1〜9/9 分。列名が「(最新)」付きで、発着信時間・顧客ID・登録者IDが無い)。
 * 日付・時間帯は「更新日時(最新)」、BB ID は「最終保存オペレーター(最新)」(例 `SR2049 (石田　夏菜)`)から取る。
 */
export const ACTIVITY_COLUMNS_ALT = {
  at: '更新日時(最新)',
  updated_at: '更新日時(最新)',
  reaction: 'リアクション情報(最新)',
  contact: '対応者情報',
  bb_id: '最終保存オペレーター(最新)',
  operator_name: '登録者名',
  business_code: '業務コード(最新)',
  phone: '電話番号(最新)',
  customer_id: '',
} as const;

const mappingOf = (columns: Record<string, string>): ColumnMapping => ({ kind: 'activity', columns: { ...columns }, options: { timeUnit: 'hms', timezone: 'JST' } });

export const DEFAULT_ACTIVITY_MAPPING: ColumnMapping = mappingOf(ACTIVITY_COLUMNS);
export const ALT_ACTIVITY_MAPPING: ColumnMapping = mappingOf(ACTIVITY_COLUMNS_ALT);

/** 列名を見て、どちらの出力形かを選ぶ */
export function activityMappingFor(headers: string[]): ColumnMapping {
  const have = new Set(headers.map((h) => h.trim()));
  return have.has('リアクション情報(最新)') && !have.has('リアクション情報') ? ALT_ACTIVITY_MAPPING : DEFAULT_ACTIVITY_MAPPING;
}

/** 対応履歴(顧客情報レポート)の列かどうか。どちらの出力形も受け付ける */
export function isActivityHeader(headers: string[]): boolean {
  const have = new Set(headers.map((h) => h.trim()));
  return (have.has('リアクション情報') && have.has('登録者ID')) || (have.has('リアクション情報(最新)') && have.has('最終保存オペレーター(最新)'));
}

/** BlueBean が「担当者なし」を表す値(この行は BB ID なしとして扱う) */
const BLANK_OPERATOR = /^[(（]?\s*空白\s*[)）]?$|^[-－]$/;

/** 「SR2049 (石田　夏菜)」→ { id: 'SR2049', name: '石田　夏菜' }。ID だけの列はそのまま返す。「（空白）」は空 */
export function splitOperatorCell(s: string): { id: string; name: string } {
  const t = s.trim();
  if (BLANK_OPERATOR.test(t)) return { id: '', name: '' };
  const m = t.match(/^([^\s(（]+)\s*[(（]([^)）]*)[)）]?\s*$/);
  if (m) return { id: BLANK_OPERATOR.test(m[1] ?? '') ? '' : (m[1] ?? ''), name: (m[2] ?? '').trim() };
  return { id: t, name: '' };
}

/** 取得と判定する リアクション情報 の値 */
export const APPOINTMENT_REACTION = 'アポ〇';
const CANCEL_CONTACT = 'キャンセル';

// 1桁の月・日・時にも対応する(2026-09-11 受領の顧客情報レポートは `2026-09-09 9:42:47` のように時が1桁)
const DATETIME_RE = /^(\d{4})-(\d{1,2})-(\d{1,2})[ T](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/;

/** 'YYYY-MM-DD HH:MM:SS' を検証して正規化。読めなければ null */
export function normalizeDateTime(s: string | undefined): string | null {
  const t = (s ?? '').trim().replace(/\//g, '-');
  const m = t.match(DATETIME_RE);
  if (!m) return null;
  const p2 = (x: string) => x.padStart(2, '0');
  return `${m[1]}-${p2(m[2]!)}-${p2(m[3]!)} ${p2(m[4]!)}:${p2(m[5]!)}:${p2(m[6] ?? '00')}`;
}

/** 業務コードの表記ゆれ(前後・全角空白)を吸収する */
export function normalizeCode(s: string): string {
  return s.replace(/[\s　]/g, '');
}

/** 「アポ〇」の表記ゆれ(〇/○/◯)を吸収する */
function isAppointmentReaction(s: string): boolean {
  return s.trim().replace(/[○◯]/g, '〇') === APPOINTMENT_REACTION;
}

export const activityImporter: CsvImporter<ParsedActivityRow> = {
  kind: 'activity',

  validateHeader(headers, mapping) {
    const issues: ImportIssue[] = [];
    const have = new Set(headers.map((h) => h.trim()));
    // 空の割当(その出力形に無い列。顧客IDなど)は必須にしない
    const missing = Object.values(mapping.columns).filter((c) => c !== '' && !have.has(c));
    if (missing.length > 0) {
      issues.push({ level: 'error', code: 'header_mismatch', message: `対応履歴に必要な列がありません: ${missing.join(', ')}` });
    }
    return issues;
  },

  /** アポ〇の行だけを正規化して返す(他のリアクションは読み捨て)。個人情報は customer_key(重複判定用)にしか使わない */
  parse(rows, mapping, file) {
    const issues: ImportIssue[] = [];
    const col = (r: RawRow, key: keyof typeof ACTIVITY_COLUMNS) => (r[mapping.columns[key] ?? ''] ?? '').trim();
    const out: ParsedActivityRow[] = [];
    let noBbId = 0;
    /**
     * 登録者名 → BB ID の対応表。同じファイルの中で BB ID が入っている行から作る。
     * 「最終保存オペレーター(最新)」が「（空白）」の行を、登録者名でその人に戻すために使う
     * (2026-09-12: 9/9 の坂巻さんのアポ1件がこれで落ちていた。現行シートは登録者名で数えている)
     */
    const nameToBbId = new Map<string, string>();
    for (const r of rows) {
      const id = normalizeBbId(splitOperatorCell(col(r, 'bb_id')).id);
      const nm = col(r, 'operator_name');
      if (id && nm && !nameToBbId.has(nm)) nameToBbId.set(nm, id);
    }
    /**
     * 「(最新)」付きの出力形は **発着信時間が無く、日付は「更新日時(最新)」しか無い**。
     * 顧客情報レポートは「履歴登録日レンジ」で行が選ばれる一方、更新日時(最新)は **あとから動く**
     * (翌日以降に同じお客さまをもう一度触ると、その時刻に書き換わる)。
     * その日のうちに出せば同じだが、あとからまとめて出すと日付がずれるため、
     * **ファイルの対象日より後の更新日時は「その日の取得・時間帯は不明」として扱う**
     * (2026-09-20 発注者の出力手順「履歴登録日レンジ＝その日」に合わせる。D-96)
     */
    const recordOnly = (mapping.columns['at'] ?? '') === ACTIVITY_COLUMNS_ALT.at;
    const targetDate: string = recordOnly ? file.date : '';
    let lateRows = 0;
    rows.forEach((r, i) => {
      const rowNo = i + 2;
      if (!isAppointmentReaction(col(r, 'reaction'))) return;
      // 「最終保存オペレーター(最新)」形式(`SR2049 (石田　夏菜)`)にも対応する
      const rawOperator = col(r, 'bb_id');
      const split = splitOperatorCell(rawOperator);
      const bbId = normalizeBbId(split.id) || (nameToBbId.get(col(r, 'operator_name')) ?? '');
      if (!bbId) {
        noBbId++;
        return;
      }
      const nameCell = col(r, 'operator_name');
      const operatorName = nameCell === rawOperator ? split.name : nameCell;
      let at = normalizeDateTime(col(r, 'at'));
      let updated = normalizeDateTime(col(r, 'updated_at')) ?? at;
      if (!at || !updated) {
        issues.push({ level: 'warning', row: rowNo, code: 'bad_datetime', message: `${bbId}: 発着信時間／更新日の形式が読めないため、この行を除外しました` });
        return;
      }
      if (targetDate !== '' && updated.slice(0, 10) > targetDate) {
        // 対象日の取得として数え、時間帯は「時間外」にする(00:00 は HOUR_BANDS の外)
        at = `${targetDate} 00:00:00`;
        updated = at;
        lateRows++;
      }
      const phone = col(r, 'phone').replace(/\D/g, '');
      const customerId = col(r, 'customer_id');
      const customerKey = phone !== '' ? `tel:${phone}` : customerId !== '' ? `cid:${customerId}` : `row:${rowNo}`;
      if (customerKey.startsWith('row:')) {
        issues.push({ level: 'warning', row: rowNo, code: 'no_customer_key', message: `${bbId}: 電話番号(最新)も顧客IDも空のため、重複判定ができません(1件として数えます)` });
      }
      out.push({
        at,
        updated_at: updated,
        bb_id: bbId,
        operator_name: operatorName,
        business_code: normalizeCode(col(r, 'business_code')),
        customer_key: customerKey,
        is_cancel: col(r, 'contact') === CANCEL_CONTACT,
        source_row: rowNo,
      });
    });
    if (noBbId > 0) {
      issues.push({ level: 'warning', code: 'no_bb_id', message: `BB ID(登録者ID／最終保存オペレーター)が空のアポ〇の行が ${noBbId} 件あり、除外しました` });
    }
    if (lateRows > 0) {
      issues.push({
        level: 'warning',
        code: 'late_update',
        message: `更新日時(最新)が ${targetDate.replaceAll('-', '/')} より後の行が ${lateRows} 件あります(あとからお客さまの情報が更新されたもの)。この日の取得として数え、時間帯は「時間外」にしました`,
      });
    }
    const info: ImportFileInfo = { ...file, kind: 'activity' };
    const result: ImportResult<ParsedActivityRow> = { rows: out, issues, file: info };
    return result;
  },
};

export type ActivityAggregate = {
  /** bb_id → 業務コード → 集約値 */
  stats: Map<string, Map<string, ActivityStat>>;
  issues: ImportIssue[];
  /** このファイルに含まれていた業務コード(取込の差し替え単位) */
  business_codes: string[];
  totals: {
    /** アポ〇の行数 */
    rows: number;
    /** 重複除去後の取得件数 */
    appointments: number;
    duplicates: number;
    other_date: number;
    outside: number;
    cancel: number;
  };
};

/** 業務コード → 会社ID。会社マスタの BB業務コード、無ければ社名で照合 */
export function resolveCompanyByCode(code: string, companies: Company[]): Company | null {
  const key = normalizeCode(code);
  if (key === '') return null;
  const byCode = companies.find((c) => c.bb_business_codes.some((x) => normalizeCode(x) === key));
  if (byCode) return byCode;
  return companies.find((c) => normalizeCode(c.name) === key) ?? null;
}

/**
 * アポ〇の行を「顧客はその日1件」に絞って BB ID × 業務コード で集計する。
 * 同じ顧客の複数行は 更新日 が最後の行を採用(現行シートの「最終保存オペレーター」「更新日時(最新)」に相当)。
 */
export function aggregateActivity(rows: ParsedActivityRow[], opts: { date: DateStr; companies: Company[] }): ActivityAggregate {
  const issues: ImportIssue[] = [];
  const { date, companies } = opts;

  // 1) 対象日以外の行は除外(出力レンジの設定ミスを検出)
  const sameDay = rows.filter((r) => r.at.slice(0, 10) === date);
  const otherDate = rows.length - sameDay.length;
  if (otherDate > 0) {
    issues.push({ level: 'warning', code: 'other_date', message: `発着信時間が ${date.replaceAll('-', '/')} 以外の行が ${otherDate} 件あり、除外しました(出力の日付レンジを確認してください)` });
  }

  // 2) 顧客ごとに最後に更新した行を採用
  const latest = new Map<string, ParsedActivityRow>();
  for (const r of sameDay) {
    const prev = latest.get(r.customer_key);
    if (!prev || r.updated_at > prev.updated_at || (r.updated_at === prev.updated_at && r.source_row > prev.source_row)) latest.set(r.customer_key, r);
  }
  const adopted = [...latest.values()].sort((a, b) => a.source_row - b.source_row);
  const duplicates = sameDay.length - adopted.length;

  // 3) BB ID × 業務コード に集計
  const stats = new Map<string, Map<string, ActivityStat>>();
  const companyByCode = new Map<string, Company | null>();
  const codes = new Set<string>();
  let outside = 0;
  let cancel = 0;
  for (const r of adopted) {
    codes.add(r.business_code);
    if (!companyByCode.has(r.business_code)) companyByCode.set(r.business_code, resolveCompanyByCode(r.business_code, companies));
    const company = companyByCode.get(r.business_code) ?? null;
    const perBb = stats.get(r.bb_id) ?? new Map<string, ActivityStat>();
    const stat: ActivityStat = perBb.get(r.business_code) ?? {
      business_code: r.business_code,
      company_id: company?.id ?? null,
      appointments: 0,
      by_hour: HOUR_BANDS.map(() => 0),
      outside_count: 0,
      cancel_count: 0,
      source_rows: [],
    };
    stat.appointments++;
    // 時間帯は更新日の時台(更新日が別日なら発着信時間の時台)
    const stamp = r.updated_at.slice(0, 10) === date ? r.updated_at : r.at;
    const hour = Number(stamp.slice(11, 13));
    const idx = (HOUR_BANDS as readonly number[]).indexOf(hour);
    if (idx >= 0) stat.by_hour[idx] = (stat.by_hour[idx] ?? 0) + 1;
    else {
      stat.outside_count++;
      outside++;
    }
    if (r.is_cancel) {
      stat.cancel_count++;
      cancel++;
    }
    stat.source_rows.push(r.source_row);
    perBb.set(r.business_code, stat);
    stats.set(r.bb_id, perBb);
  }
  for (const [code, company] of companyByCode) {
    if (!company) {
      issues.push({
        level: 'warning',
        code: 'no_company',
        message: code === '' ? '業務コードが空の行があります(割振なしで取り込みます)' : `業務コード「${code}」に当たる会社が会社マスタにありません(割振なしで取り込みます。会社マスタの BB業務コードに登録してください)`,
      });
    }
  }
  return {
    stats,
    issues,
    business_codes: [...codes].sort(),
    totals: { rows: rows.length, appointments: adopted.length, duplicates, other_date: otherDate, outside, cancel },
  };
}
